import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/features/auth/hooks/use-auth';
import { UserRole, Permission } from '@/types';
import { Loader2 } from 'lucide-react';
import { LockedModule } from '@/components/locked-module';
import {
  abreConVerReglas,
  estaEnElRegistro,
  getMenuKeyForPath,
  getPermissionForPath,
} from './route-modules';
import { permisosDeReglasDe } from '@/features/pricing-rules/lib/use-permisos-de-reglas';
import { motivoDelBloqueo } from '@/features/auth/tenant-modules';
import { miEmpresaAbreEnLaDemo } from '@/features/mini-prixia/empresa-demo';

interface ProtectedRouteProps {
  children: ReactNode;
  // Override opcional del módulo. Si no se pasa, se deriva del pathname.
  // Si el módulo está bloqueado para el rol, se muestra LockedModule en lugar
  // del contenido (bloquea también el acceso por URL directa).
  menuKey?: string;
}

/**
 * HOC para proteger rutas que requieren autenticación
 */
export function ProtectedRoute({ children, menuKey }: ProtectedRouteProps) {
  const { user, isAuthenticated, isLoading, isModuleLocked, menuItems, permissions } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  /*
    2.7b: cerrado por defecto. Una ruta que no declaró módulo, permiso ni `libre`
    (ver `route-modules.ts`) y no trae `menuKey` explícito no se abre: antes pasaba
    sin que nadie lo notara. El guardián de `route-modules.test.ts` evita que el
    router tenga una así.
  */
  if (!menuKey && !estaEnElRegistro(location.pathname)) {
    return <LockedModule motivo="sin-registrar" />;
  }

  const moduleKey = menuKey ?? getMenuKeyForPath(location.pathname);
  // P3-D10 (27-sep): Estrategias de Mercado también abre con ver reglas (Reglas
  // vive adentro); la página muestra entonces sólo Reglas y Productos. Sin la
  // lista de permisos todavía no se sabe: manda el módulo, como siempre.
  // El candado del rol («Bloqueado») lo respeta la página (T1 Step 4, A1).
  const entraPorReglas =
    abreConVerReglas(location.pathname) &&
    permissions.length > 0 &&
    permisosDeReglasDe((p) => permissions.includes(p)).ver;

  if (moduleKey && !entraPorReglas) {
    // Bloqueado por el rol: el cliente decidió que este rol no lo use.
    if (isModuleLocked(moduleKey)) {
      return <LockedModule motivo="rol" />;
    }

    /*
      Escondido porque la empresa no lo compró.

      🔴 Antes acá solo se miraba el candado, así que esconder un módulo del menú
      NO bloqueaba la dirección: bastaba escribir la URL para entrar a un módulo
      que la empresa no tiene.

      ⚠ Se compara contra la lista CRUDA y no con `canSeeMenu`. Ese helper tiene
      un respaldo —si la lista todavía no llegó, solo el admin de empresa ve
      todo— que como guarda de ruta sería peligroso: un vendedor cuya sesión
      arrancó sin la lista quedaría afuera de la aplicación entera. Acá la regla
      es al revés: **si no sabemos, no se bloquea**.

      Que esto falle abierto no abre un hueco de seguridad, porque el servidor
      también aplica el techo (ver `checkPermission` en el backend). Esta guarda
      es de experiencia de uso: evita mostrar una pantalla vacía. La que manda es
      la del servidor.
    */
    const sabemosQueModulosTiene = menuItems.length > 0;
    // Una demo abre «Mi empresa» sin el módulo `admin`: la página le muestra sólo Usuarios.
    const laAbreLaDemo = miEmpresaAbreEnLaDemo(location.pathname);
    if (sabemosQueModulosTiene && !laAbreLaDemo && !menuItems.includes(`menu:${moduleKey}`)) {
      /*
        🔴 Acá decía siempre "no-incluido", y para casi todos los casos era falso.
        Un vendedor que abría /permisos leía "Este módulo no está incluido" —que
        significa "tu empresa no lo compró, hablá con DATEC"— cuando la verdad era
        que su propio administrador se lo podía dar. Lo mandaba a reclamarle a la
        persona equivocada.

        `motivoDelBloqueo` compara contra el techo real de la empresa: si el
        módulo está contratado pero el rol no lo tiene, el cartel manda al
        administrador de la empresa; si no está contratado, a DATEC.
      */
      return <LockedModule motivo={motivoDelBloqueo(moduleKey)} />;
    }
  }

  /*
    Las rutas que se guían por un permiso (Reglas, desde el 26-sep). Misma
    regla que arriba: **si no sabemos, no se bloquea** (una sesión vieja sin la
    lista); el servidor responde 403 igual (`permisos-reglas.middleware.ts`).
  */
  const porPermiso = getPermissionForPath(location.pathname);
  if (
    porPermiso &&
    user?.role !== 'PLATFORM_ADMIN' &&
    permissions.length > 0 &&
    !permissions.includes(porPermiso.permiso)
  ) {
    return <LockedModule motivo={motivoDelBloqueo(porPermiso.modulo)} />;
  }

  return <>{children}</>;
}

interface RequireRoleProps {
  children: ReactNode;
  role: UserRole | UserRole[];
}

/**
 * HOC para proteger rutas por rol específico
 */
export function RequireRole({ children, role }: RequireRoleProps) {
  const { user, hasRole } = useAuth();
  const location = useLocation();

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  const roles = Array.isArray(role) ? role : [role];
  const hasRequiredRole = roles.some((r) => hasRole(r));

  if (!hasRequiredRole) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="text-center space-y-4">
          <h1 className="text-4xl font-bold text-destructive">Acceso Denegado</h1>
          <p className="text-muted-foreground">No tienes permisos para acceder a esta página</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

interface RequirePermissionProps {
  children: ReactNode;
  permission: Permission;
}

/**
 * HOC para proteger rutas por permiso específico
 */
export function RequirePermission({ children, permission }: RequirePermissionProps) {
  const { user, hasPermission } = useAuth();
  const location = useLocation();

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (!hasPermission(permission)) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="text-center space-y-4">
          <h1 className="text-4xl font-bold text-destructive">Acceso Denegado</h1>
          <p className="text-muted-foreground">
            No tienes el permiso necesario para acceder a esta página
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
